import { useState, Fragment } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "@/lib/toast";
import { ShieldCheck, Search, Trash2, Download, Loader2, RefreshCw, Eraser, Inbox, Copy, Check, BookOpen } from "lucide-react";

const REQUEST_TYPE_HELP = {
  access: {
    label: "Access",
    instruction: "Look up the email in this page, then click 'Export as ZIP' to download every record we hold (data + image files). Send that ZIP to the user. Mark this request 'Completed'.",
  },
  erasure: {
    label: "Erasure",
    instruction: "Look up the email in this page, then click 'Erase all data' to wipe every submission, report, and Cloudinary asset. Confirm the deletion summary, then mark this request 'Completed'.",
  },
  rectification: {
    label: "Rectification",
    instruction: "Read the user's 'details' (what they want corrected). Apply the change manually in the relevant admin page (Contacts, False ad reports, etc.). Note the change in admin_notes and mark this request 'Completed'.",
  },
  restriction: {
    label: "Restriction",
    instruction: "Read the details for what should be restricted from further processing. There is no automated flag — note the restriction in admin_notes and ensure the relevant records are not used for any active processing (e.g. unpublish a false-ad report). Mark this request 'Completed'.",
  },
};

const REQUEST_STATUS_BADGE = {
  pending_email_confirmation: { label: "Awaiting confirmation", color: "bg-amber-100 text-amber-900" },
  email_confirmed: { label: "Verified — ready to process", color: "bg-emerald-100 text-emerald-900" },
  completed: { label: "Completed", color: "bg-[#F1F3F5] text-[#6C757D]" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-900" },
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminGdpr() {
  const [email, setEmail] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const qc = useQueryClient();

  const lookupQ = useQuery({
    enabled: !!submittedEmail,
    queryKey: ["gdpr-lookup", submittedEmail],
    queryFn: async () => (await api.get("/admin/gdpr/lookup", { params: { email: submittedEmail } })).data,
  });

  const auditQ = useQuery({
    queryKey: ["gdpr-audit"],
    queryFn: async () => (await api.get("/admin/gdpr/audit-log", { params: { limit: 50 } })).data,
  });

  const requestsQ = useQuery({
    queryKey: ["gdpr-requests"],
    queryFn: async () => (await api.get("/admin/gdpr/requests", { params: { limit: 100 } })).data,
  });

  const patchRequestMut = useMutation({
    mutationFn: async ({ id, body }) => (await api.patch(`/admin/gdpr/requests/${id}`, body)).data,
    onSuccess: () => {
      toast.success("Request updated.");
      qc.invalidateQueries({ queryKey: ["gdpr-requests"] });
      qc.invalidateQueries({ queryKey: ["gdpr-audit"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Update failed"),
  });

  const orphansQ = useQuery({
    queryKey: ["gdpr-orphans"],
    enabled: false,
    queryFn: async () => (await api.get("/admin/gdpr/orphans")).data,
  });

  const deleteMut = useMutation({
    mutationFn: async () => (await api.delete("/admin/gdpr/by-email", { params: { email: submittedEmail } })).data,
    onSuccess: (r) => {
      toast.success(`Erased ${r.deleted_contact_submissions} submission(s), ${r.deleted_false_advertising_reports} report(s), ${r.cloudinary_assets.length} asset(s).`);
      setConfirmDelete(false);
      qc.invalidateQueries({ queryKey: ["gdpr-lookup"] });
      qc.invalidateQueries({ queryKey: ["gdpr-audit"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Delete failed"),
  });

  const purgeOrphansMut = useMutation({
    mutationFn: async () => (await api.delete("/admin/gdpr/orphans")).data,
    onSuccess: (r) => {
      toast.success(`Purged ${r.deleted} orphan asset(s) of ${r.scanned} scanned.`);
      qc.invalidateQueries({ queryKey: ["gdpr-orphans"] });
      qc.invalidateQueries({ queryKey: ["gdpr-audit"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Purge failed"),
  });

  const retentionMut = useMutation({
    mutationFn: async () => (await api.post("/admin/gdpr/run-retention")).data,
    onSuccess: (r) => {
      if (r.skipped) {
        toast.info("Retention is disabled (set retention months in Settings).");
      } else {
        toast.success(`Retention run: deleted ${r.deleted_contact_submissions} submission(s), destroyed ${r.destroyed_cloudinary_assets} asset(s).`);
      }
      qc.invalidateQueries({ queryKey: ["gdpr-audit"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Retention run failed"),
  });

  function onLookup(e) {
    e.preventDefault();
    setSubmittedEmail(email.trim().toLowerCase());
    setConfirmDelete(false);
  }

  function downloadExport() {
    const url = `${api.defaults.baseURL}/admin/gdpr/export?email=${encodeURIComponent(submittedEmail)}`;
    const token = localStorage.getItem("sc_admin_token");
    // Fetch then trigger save because we need to send the Authorization header
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then((blob) => {
        const a = document.createElement("a");
        const u = URL.createObjectURL(blob);
        a.href = u;
        a.download = `gdpr-export-${submittedEmail}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(u);
      })
      .catch((e) => toast.error(`Export failed: ${e.message}`));
  }

  return (
    <div className="space-y-6" data-testid="admin-gdpr">
      <header>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-[#1B4332]" /> GDPR
        </h1>
        <p className="text-sm text-[#6C757D] mt-1">Honor data-subject requests, enforce retention, and clean up orphan files.</p>
      </header>

      {/* Public-submitted GDPR requests */}
      <RequestsSection
        data={requestsQ.data}
        isLoading={requestsQ.isLoading}
        onPatch={(id, body) => patchRequestMut.mutate({ id, body })}
        isPatching={patchRequestMut.isPending}
        onLookupRequest={(email) => {
          setEmail(email);
          setSubmittedEmail(email.toLowerCase().trim());
          window.scrollTo({ top: 400, behavior: "smooth" });
        }}
      />

      {/* Lookup */}
      <section className="bg-white border border-[#E9ECEF] rounded-md p-5">
        <h2 className="font-semibold mb-3 flex items-center gap-2"><Search className="w-4 h-4" /> Lookup by email</h2>
        <form onSubmit={onLookup} className="flex gap-2">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            className="flex-1 border border-[#E9ECEF] rounded-md px-3 py-2 text-sm"
            data-testid="gdpr-email-input"
          />
          <button type="submit" className="bg-[#1B4332] text-white px-4 py-2 text-sm rounded-md" data-testid="gdpr-lookup-button">
            Look up
          </button>
        </form>

        {lookupQ.isLoading && <div className="mt-4 text-sm text-[#6C757D] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Searching…</div>}
        {lookupQ.isError && <div className="mt-4 text-sm text-red-700">Lookup failed.</div>}

        {lookupQ.data && (
          <div className="mt-4 space-y-3" data-testid="gdpr-results">
            <div className="text-sm">
              <span className="font-semibold">Found:</span>{" "}
              <span data-testid="gdpr-count-submissions">{lookupQ.data.counts.contact_submissions}</span> submission(s),{" "}
              <span data-testid="gdpr-count-reports">{lookupQ.data.counts.false_advertising_reports}</span> false-ad report(s),{" "}
              <span data-testid="gdpr-count-files">{lookupQ.data.counts.image_files}</span> image file(s).
              <div className="text-xs text-[#6C757D] mt-1">Hash: <code>{lookupQ.data.email_hash.slice(0, 12)}…</code></div>
            </div>

            {lookupQ.data.contact_submissions.length > 0 && (
              <div className="border border-[#E9ECEF] rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8F9FA] text-left text-xs uppercase text-[#6C757D]">
                    <tr>
                      <th className="px-3 py-2">When</th>
                      <th className="px-3 py-2">Category</th>
                      <th className="px-3 py-2">Subject</th>
                      <th className="px-3 py-2 text-right">Files</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lookupQ.data.contact_submissions.map((s) => (
                      <tr key={s.id} className="border-t border-[#E9ECEF]" data-testid={`gdpr-submission-${s.id}`}>
                        <td className="px-3 py-2 whitespace-nowrap">{fmt(s.created_at)}</td>
                        <td className="px-3 py-2">{s.category}</td>
                        <td className="px-3 py-2 truncate max-w-[300px]">{s.subject || <span className="text-[#6C757D] italic">(no subject)</span>}</td>
                        <td className="px-3 py-2 text-right">{(s.file_urls || []).length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-2">
              <button
                type="button"
                onClick={downloadExport}
                className="inline-flex items-center gap-1.5 border border-[#E9ECEF] px-3 py-2 text-sm rounded-md hover:bg-[#F1F3F5]"
                data-testid="gdpr-export-button"
              >
                <Download className="w-4 h-4" /> Export as ZIP
              </button>
              {!confirmDelete ? (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  disabled={lookupQ.data.counts.contact_submissions === 0 && lookupQ.data.counts.false_advertising_reports === 0}
                  className="inline-flex items-center gap-1.5 bg-red-600 text-white px-3 py-2 text-sm rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="gdpr-delete-button"
                >
                  <Trash2 className="w-4 h-4" /> Erase all data
                </button>
              ) : (
                <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 px-3 py-2 rounded-md text-sm">
                  <span>Permanently erase everything for <strong>{submittedEmail}</strong>?</span>
                  <button
                    type="button"
                    onClick={() => deleteMut.mutate()}
                    disabled={deleteMut.isPending}
                    className="bg-red-600 text-white px-3 py-1 text-xs rounded"
                    data-testid="gdpr-delete-confirm-button"
                  >
                    {deleteMut.isPending ? "Erasing…" : "Confirm"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="border border-[#E9ECEF] px-3 py-1 text-xs rounded bg-white"
                    data-testid="gdpr-delete-cancel-button"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Retention enforcer */}
      <section className="bg-white border border-[#E9ECEF] rounded-md p-5">
        <h2 className="font-semibold mb-2 flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Retention policy</h2>
        <p className="text-sm text-[#6C757D] mb-3">
          The retention enforcer runs automatically every 24 hours, deleting contact submissions older
          than the window configured in <a href="/admin/settings" className="underline">Settings → GDPR</a>. Use the button below to trigger it now.
        </p>
        <button
          type="button"
          onClick={() => retentionMut.mutate()}
          disabled={retentionMut.isPending}
          className="inline-flex items-center gap-1.5 border border-[#E9ECEF] px-3 py-2 text-sm rounded-md hover:bg-[#F1F3F5]"
          data-testid="gdpr-run-retention-button"
        >
          <RefreshCw className={`w-4 h-4 ${retentionMut.isPending ? "animate-spin" : ""}`} />
          {retentionMut.isPending ? "Running…" : "Run retention now"}
        </button>
      </section>

      {/* Orphans */}
      <section className="bg-white border border-[#E9ECEF] rounded-md p-5">
        <h2 className="font-semibold mb-2 flex items-center gap-2"><Eraser className="w-4 h-4" /> Cloudinary orphan scan</h2>
        <p className="text-sm text-[#6C757D] mb-3">Find image files on Cloudinary that no longer have a matching submission row (e.g. after a manual DB cleanup).</p>
        <div className="flex gap-2 mb-3">
          <button
            type="button"
            onClick={() => orphansQ.refetch()}
            disabled={orphansQ.isFetching}
            className="inline-flex items-center gap-1.5 border border-[#E9ECEF] px-3 py-2 text-sm rounded-md hover:bg-[#F1F3F5]"
            data-testid="gdpr-orphans-scan-button"
          >
            {orphansQ.isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Scan
          </button>
          {orphansQ.data && orphansQ.data.orphans.length > 0 && (
            <button
              type="button"
              onClick={() => { if (confirm(`Permanently delete ${orphansQ.data.orphans.length} orphan asset(s)?`)) purgeOrphansMut.mutate(); }}
              disabled={purgeOrphansMut.isPending}
              className="inline-flex items-center gap-1.5 bg-red-600 text-white px-3 py-2 text-sm rounded-md"
              data-testid="gdpr-orphans-purge-button"
            >
              <Trash2 className="w-4 h-4" /> Purge {orphansQ.data.orphans.length}
            </button>
          )}
        </div>
        {orphansQ.data && (
          <div className="text-sm" data-testid="gdpr-orphans-result">
            Scanned <strong>{orphansQ.data.scanned}</strong> asset(s), found <strong>{orphansQ.data.orphans.length}</strong> orphan(s).
            {orphansQ.data.orphans.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs">
                {orphansQ.data.orphans.slice(0, 20).map((o) => (
                  <li key={o.public_id} className="font-mono text-[#6C757D] truncate">{o.public_id}</li>
                ))}
                {orphansQ.data.orphans.length > 20 && <li className="text-[#6C757D]">…and {orphansQ.data.orphans.length - 20} more</li>}
              </ul>
            )}
          </div>
        )}
      </section>

      {/* Audit log */}
      <section className="bg-white border border-[#E9ECEF] rounded-md p-5" data-testid="gdpr-audit-log">
        <h2 className="font-semibold mb-3">Audit log</h2>
        <p className="text-xs text-[#6C757D] mb-3">Every GDPR action is recorded here for compliance. Email hashes are stored — never raw addresses.</p>
        {auditQ.isLoading && <div className="text-sm text-[#6C757D]">Loading…</div>}
        {auditQ.data && (
          <div className="border border-[#E9ECEF] rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F8F9FA] text-left text-xs uppercase text-[#6C757D]">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Target hash</th>
                  <th className="px-3 py-2">Actor</th>
                  <th className="px-3 py-2">Details</th>
                </tr>
              </thead>
              <tbody>
                {auditQ.data.items.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-[#6C757D]">No GDPR actions yet.</td></tr>
                )}
                {auditQ.data.items.map((row) => (
                  <tr key={row.id} className="border-t border-[#E9ECEF]" data-testid={`gdpr-audit-row-${row.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">{fmt(row.at)}</td>
                    <td className="px-3 py-2"><span className="inline-block bg-[#F1F3F5] px-2 py-0.5 rounded text-xs">{row.action}</span></td>
                    <td className="px-3 py-2 font-mono text-xs">{row.email_hash ? `${row.email_hash.slice(0, 10)}…` : "—"}</td>
                    <td className="px-3 py-2 text-xs">{row.actor}</td>
                    <td className="px-3 py-2 text-xs text-[#6C757D]"><code className="whitespace-pre-wrap break-words">{JSON.stringify(row.details)}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function RequestsSection({ data, isLoading, onPatch, isPatching, onLookupRequest }) {
  const [expandedId, setExpandedId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [notesDraft, setNotesDraft] = useState({});

  function copyConfirmUrl(token, id) {
    const url = `${window.location.origin}/gdpr/confirm/${token}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      toast.success("Confirmation URL copied to clipboard. Paste it into an email to the user.");
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 3000);
    });
  }

  const items = data?.items || [];
  const pending = items.filter((r) => r.status === "pending_email_confirmation").length;
  const verified = items.filter((r) => r.status === "email_confirmed").length;

  return (
    <section className="bg-white border border-[#E9ECEF] rounded-md p-5" data-testid="gdpr-requests-section">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-semibold flex items-center gap-2"><Inbox className="w-4 h-4" /> Data-subject requests</h2>
        <div className="text-xs text-[#6C757D]">
          <span data-testid="gdpr-requests-pending">{pending}</span> awaiting email confirmation ·
          {" "}<span data-testid="gdpr-requests-verified">{verified}</span> verified
        </div>
      </div>
      <p className="text-xs text-[#6C757D] mb-4">
        Requests submitted via the public site (Contact page or Privacy page). After a request is submitted, copy its confirmation URL and email it to the user from your own inbox; once they click it, the request moves to <em>Verified — ready to process</em>. Click any row to see the per-type instructions.
      </p>

      <details className="mb-4 text-xs" data-testid="gdpr-request-help">
        <summary className="cursor-pointer text-[#6C757D] hover:text-[#1B4332] inline-flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> What each request type means</summary>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          {Object.entries(REQUEST_TYPE_HELP).map(([k, v]) => (
            <div key={k} className="border border-[#E9ECEF] rounded-md p-3 bg-[#F8F9FA]">
              <div className="font-semibold text-[#1B4332] mb-1">{v.label}</div>
              <p className="text-[#6C757D] leading-relaxed">{v.instruction}</p>
            </div>
          ))}
        </div>
      </details>

      {isLoading && <div className="text-sm text-[#6C757D]">Loading…</div>}
      {!isLoading && items.length === 0 && (
        <div className="text-sm text-[#6C757D] py-6 text-center border border-dashed border-[#E9ECEF] rounded-md">No requests yet.</div>
      )}
      {items.length > 0 && (
        <div className="border border-[#E9ECEF] rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FA] text-left text-xs uppercase text-[#6C757D]">
              <tr>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const badge = REQUEST_STATUS_BADGE[r.status] || { label: r.status, color: "bg-[#F1F3F5]" };
                const isExpanded = expandedId === r.id;
                const help = REQUEST_TYPE_HELP[r.request_type];
                return (
                  <Fragment key={r.id}>
                    <tr className="border-t border-[#E9ECEF] hover:bg-[#F8F9FA]" data-testid={`gdpr-request-row-${r.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs">{fmt(r.created_at)}</td>
                      <td className="px-3 py-2 text-xs break-all">{r.email}</td>
                      <td className="px-3 py-2 text-xs">{help?.label || r.request_type}</td>
                      <td className="px-3 py-2"><span className={`inline-block px-2 py-0.5 rounded text-xs ${badge.color}`}>{badge.label}</span></td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => setExpandedId(isExpanded ? null : r.id)}
                          className="text-xs underline text-[#6C757D] hover:text-[#1B4332]"
                          data-testid={`gdpr-request-expand-${r.id}`}
                        >
                          {isExpanded ? "Hide" : "View"}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-[#F8F9FA] border-t border-[#E9ECEF]">
                        <td colSpan={5} className="px-3 py-4">
                          <div className="space-y-3 text-sm">
                            <div className="bg-white border border-[#E9ECEF] rounded p-3">
                              <div className="text-xs font-semibold text-[#1B4332] mb-1">How to handle a {help?.label || r.request_type} request</div>
                              <p className="text-xs text-[#6C757D] leading-relaxed">{help?.instruction}</p>
                            </div>

                            {r.details && (
                              <div>
                                <div className="text-xs uppercase tracking-wider text-[#6C757D] mb-1">User-provided details</div>
                                <div className="text-xs bg-white border border-[#E9ECEF] rounded p-2 whitespace-pre-wrap">{r.details}</div>
                              </div>
                            )}

                            {r.status === "pending_email_confirmation" && (
                              <div className="bg-amber-50 border border-amber-200 rounded p-3 flex items-center justify-between gap-2">
                                <div className="text-xs">
                                  <strong>Copy the confirmation URL below</strong> and email it to <code className="bg-white px-1 py-0.5 rounded">{r.email}</code> so they can confirm ownership.
                                </div>
                                <button
                                  type="button"
                                  onClick={() => copyConfirmUrl(r.confirmation_token, r.id)}
                                  className="inline-flex items-center gap-1.5 bg-[#1B4332] text-white px-3 py-1.5 text-xs rounded shrink-0"
                                  data-testid={`gdpr-request-copy-url-${r.id}`}
                                >
                                  {copiedId === r.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                  {copiedId === r.id ? "Copied" : "Copy URL"}
                                </button>
                              </div>
                            )}

                            <div>
                              <div className="text-xs uppercase tracking-wider text-[#6C757D] mb-1">Admin notes</div>
                              <textarea
                                rows={2}
                                value={notesDraft[r.id] ?? r.admin_notes ?? ""}
                                onChange={(e) => setNotesDraft((d) => ({ ...d, [r.id]: e.target.value }))}
                                placeholder="Internal notes — e.g. 'Sent confirmation 2026-06-15. Erased on 2026-06-18 (deleted 2 rows).'"
                                className="w-full text-xs border border-[#E9ECEF] rounded p-2"
                                data-testid={`gdpr-request-notes-${r.id}`}
                              />
                              <button
                                type="button"
                                onClick={() => onPatch(r.id, { admin_notes: notesDraft[r.id] ?? "" })}
                                disabled={isPatching || (notesDraft[r.id] ?? r.admin_notes ?? "") === (r.admin_notes ?? "")}
                                className="mt-1 text-xs text-[#1B4332] underline disabled:text-[#6C757D] disabled:no-underline"
                                data-testid={`gdpr-request-save-notes-${r.id}`}
                              >
                                Save notes
                              </button>
                            </div>

                            <div className="flex flex-wrap gap-2 pt-1">
                              {r.status === "email_confirmed" && (
                                <button
                                  type="button"
                                  onClick={() => onLookupRequest(r.email)}
                                  className="text-xs border border-[#E9ECEF] px-3 py-1.5 rounded hover:bg-white"
                                  data-testid={`gdpr-request-lookup-${r.id}`}
                                >
                                  Look up this email below ↓
                                </button>
                              )}
                              {r.status !== "completed" && r.status !== "rejected" && (
                                <button
                                  type="button"
                                  onClick={() => onPatch(r.id, { status: "completed" })}
                                  disabled={isPatching}
                                  className="text-xs bg-emerald-600 text-white px-3 py-1.5 rounded"
                                  data-testid={`gdpr-request-complete-${r.id}`}
                                >
                                  Mark Completed
                                </button>
                              )}
                              {r.status !== "rejected" && r.status !== "completed" && (
                                <button
                                  type="button"
                                  onClick={() => { if (confirm("Reject this request?")) onPatch(r.id, { status: "rejected" }); }}
                                  disabled={isPatching}
                                  className="text-xs border border-red-200 text-red-700 px-3 py-1.5 rounded"
                                  data-testid={`gdpr-request-reject-${r.id}`}
                                >
                                  Reject
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
